import React, { useState, useEffect } from "react";
import { Slider } from "../../../ui";

const ModelsSetupPanel = ({
  temperature = 0.7,
  topP = 1.0,
  instructions = "",
  outputLength = 2000,
  onTemperatureChange,
  onTopPChange,
  onInstructionsChange,
  onOutputLengthChange,
  onReset,
}) => {
  const [localTemperature, setLocalTemperature] = useState(temperature);
  const [localTopP, setLocalTopP] = useState(topP);
  const [localInstructions, setLocalInstructions] = useState(instructions);
  const [localOutputLength, setLocalOutputLength] = useState(outputLength);

  // Sync local state when props change (e.g., when reset is called from parent)
  useEffect(() => {
    setLocalTemperature(temperature);
  }, [temperature]);

  useEffect(() => {
    setLocalTopP(topP);
  }, [topP]);

  useEffect(() => {
    setLocalInstructions(instructions);
  }, [instructions]);

  useEffect(() => {
    setLocalOutputLength(outputLength);
  }, [outputLength]);

  const handleTemperatureChange = (value) => {
    setLocalTemperature(value);
    onTemperatureChange?.(value);
  };

  const handleTopPChange = (value) => {
    setLocalTopP(value);
    onTopPChange?.(value);
  };

  const handleInstructionsChange = (value) => {
    setLocalInstructions(value);
    onInstructionsChange?.(value);
  };

  const handleOutputLengthChange = (value) => {
    setLocalOutputLength(value);
    onOutputLengthChange?.(value);
  };

  const handleReset = () => {
    setLocalTemperature(0.7);
    setLocalTopP(1.0);
    setLocalInstructions("");
    setLocalOutputLength(2000);
    onTemperatureChange?.(0.7);
    onTopPChange?.(1.0);
    onInstructionsChange?.("");
    onOutputLengthChange?.(2000);
    onReset?.();
  };

  return (
    <div className="h-full flex flex-col bg-bg-50">
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
        {/* Temperature Control */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label
              className="text-sm font-medium text-fg-50"
              title="Controls randomness. Lower values make responses more focused and deterministic."
            >
              Temperature
            </label>
            <input
              type="number"
              min="0"
              max="2"
              step="0.01"
              value={localTemperature}
              onChange={(e) =>
                handleTemperatureChange(parseFloat(e.target.value) || 0)
              }
              className="w-20 px-2 py-1 text-sm text-fg-50 bg-bg-70 rounded focus:outline-none focus:ring-2 focus:ring-ac-02"
            />
          </div>
          <Slider
            min={0}
            max={2}
            step={0.01}
            value={localTemperature}
            onChange={handleTemperatureChange}
          />
        </div>

        {/* Top-P Control */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label
              className="text-sm font-medium text-fg-50"
              title="Controls diversity by considering only the top tokens that make up the specified probability mass."
            >
              Top-P
            </label>
            <input
              type="number"
              min="0.1"
              max="1"
              step="0.01"
              value={localTopP}
              onChange={(e) =>
                handleTopPChange(parseFloat(e.target.value) || 0.1)
              }
              className="w-20 px-2 py-1 text-sm text-fg-50 bg-bg-70 rounded focus:outline-none focus:ring-2 focus:ring-ac-02"
            />
          </div>
          <Slider
            min={0.1}
            max={1}
            step={0.01}
            value={localTopP}
            onChange={handleTopPChange}
          />
        </div>

        {/* Output Length Control */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label
              className="text-sm font-medium text-fg-50"
              title="Maximum number of tokens in the response."
            >
              Output Length
            </label>
            <input
              type="number"
              min="100"
              max="4000"
              step="100"
              value={localOutputLength}
              onChange={(e) =>
                handleOutputLengthChange(parseInt(e.target.value) || 100)
              }
              className="w-20 px-2 py-1 text-sm text-fg-50 bg-bg-70 rounded focus:outline-none focus:ring-2 focus:ring-ac-02"
            />
          </div>
          <Slider
            min={100}
            max={4000}
            step={100}
            value={localOutputLength}
            onChange={handleOutputLengthChange}
          />
        </div>

        {/* System Instructions */}
        <div className="space-y-3">
          <label
            className="text-sm font-medium text-fg-50"
            title="Custom instructions that will be prepended to every conversation to guide the model's behavior."
          >
            System Instructions
          </label>
          <textarea
            value={localInstructions}
            onChange={(e) => handleInstructionsChange(e.target.value)}
            placeholder="Enter custom instructions for the model behavior..."
            className="w-full h-32 p-3 bg-bg-70 rounded-lg text-sm text-fg-50 placeholder-fg-60 focus:outline-none focus:ring-2 focus:ring-ac-02 resize-none"
          />
        </div>
      </div>
    </div>
  );
};

export default ModelsSetupPanel;
